import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReferenceDropzone } from "../features/reference-load/ReferenceDropzone";
import { Button } from "../design-system/primitives/Button";
import { AboutButton } from "../features/about/AboutButton";
import { AxonResourcesCard } from "../features/brand/AxonResourcesCard";
import { Icon } from "../design-system/primitives/Icon";
import { useNbStore } from "../store/nbStore";
import { usePagesStore } from "../store/pagesStore";
import { parseSignalList } from "../lib/referenceParsers";

const IED_OPTIONS = [
  "7SL82",
  "7SL86",
  "7SL87",
  "7SA82",
  "7SA86",
  "7SA87",
  "7UT82",
  "7UT85",
  "7SK82",
  "7SK85",
  "7UM85",
  "7SJ81",
  "7SJ82",
  "7SJ85",
  "REF620",
  "REM620",
  "RET620",
  "RER620",
  "REF615",
  "REM615",
  "RET615",
  "RED615",
  "REC615",
  "REX640",
  "REL670",
  "RED670",
  "RET670",
  "REG670",
  "REC670",
  "SEL411L",
  "SEL421",
  "SEL700BT",
  "SEL700G",
  "SEL751",
  "SEL787L",
  "SEL451",
  "SEL487B",
  "SEL487E"
];

type Mode = "scratch" | "assign" | "validate";

const MODE_IDS: Mode[] = ["scratch", "assign", "validate"];

export function LandingPage() {
  const { t } = useTranslation("landing");
  const { initModels, modelsLoaded, runInference, runValidation } = useNbStore();
  const [mode,         setMode]          = useState<Mode>("scratch");
  const [iedRef,       setIedRef]        = useState("");
  const [localError,   setLocalError]    = useState("");
  const [busy,         setBusy]          = useState(false);
  const [uploadedFiles,setUploadedFiles] = useState<File[]>([]);
  const [fileNames,    setFileNames]     = useState<string[]>([]);

  useEffect(() => { void initModels(); }, [initModels]);

  // Al cambiar de modo, limpiar archivos y errores para no arrastrar estado confuso
  const switchMode = (m: Mode) => {
    setMode(m);
    setLocalError("");
    setUploadedFiles([]);
    setFileNames([]);
  };

  const handleFiles = (files: File[]) => {
    setUploadedFiles(prev => {
      const incoming = [...files];
      const isScl  = (f: File) => /\.(scd|cid|icd|iid|scl|xml)$/i.test(f.name);
      const isXlsx = (f: File) => /\.(xlsx|xls|csv|txt)$/i.test(f.name);
      const retained = prev.filter(p =>
        (incoming.some(isScl)  && isXlsx(p)) ||
        (incoming.some(isXlsx) && isScl(p))
      );
      const merged = [...retained, ...incoming];
      setFileNames(merged.map(f => f.name));
      return merged;
    });
    setLocalError("");
  };
  const getXlsx = () => uploadedFiles.find(f => /\.(xlsx|xls|csv|txt)$/i.test(f.name));
  const getScl  = () => uploadedFiles.find(f => /\.(scd|cid|icd|iid|scl|xml)$/i.test(f.name));
  const readBytes = (f: File) => f.arrayBuffer().then(b => new Uint8Array(b));

  // ── Modo "Crear desde cero": entra directo, IED opcional ────────────────────
  const enterScratch = () => {
    const id = `tab-${Date.now()}`;
    if (iedRef) useNbStore.getState().setTab(id, { selectedIed: iedRef });
    const title = iedRef ? t("titles.signalsIed", { ied: iedRef }) : t("titles.newList");
    usePagesStore.getState().open({ id, title, kind: "results" });
  };

  // ── Modo "Asignar nodos MMS": clasifica un Excel/CSV ────────────────────────
  // El IED es opcional. Con IED: clasifica de inmediato. Sin IED: entra al
  // workspace con las señales cargadas (sin clasificar) y el usuario elige el
  // IED dentro — el panel derecho re-clasifica automáticamente al elegirlo.
  const runAssign = async () => {
    setLocalError("");
    if (!modelsLoaded) { setLocalError(t("errors.engineLoading")); return; }
    const xlsxFile = getXlsx();
    if (!xlsxFile) { setLocalError(t("errors.needSignals")); return; }
    setBusy(true);
    try {
      const parsed = await parseSignalList(await readBytes(xlsxFile), xlsxFile.name);
      const id = `tab-${Date.now()}`;
      if (iedRef) {
        // Con IED: clasificación inmediata
        await runInference(id, parsed.map(p => p.signal), iedRef);
        usePagesStore.getState().open({ id, title: t("titles.signalsIed", { ied: iedRef }), kind: "results" });
      } else {
        // Sin IED: cargar señales sin clasificar; el nodo MMS se asignará al
        // elegir el IED dentro del workspace.
        const rows = parsed.map(p => ({ signal: p.signal, mmsNode: p.mmsNode ?? "—", iedRef: "" }));
        useNbStore.getState().setTab(id, { rows, status: "ready" });
        usePagesStore.getState().open({ id, title: t("titles.signalsNoIed"), kind: "results" });
      }
    } finally { setBusy(false); }
  };

  // ── Modo "Validar en SCL": valida señales contra un SCL ─────────────────────
  const runValidateScl = async () => {
    setLocalError("");
    if (!modelsLoaded) { setLocalError(t("errors.engineLoading")); return; }
    const xlsxFile = getXlsx(), sclFile = getScl();
    if (!xlsxFile) { setLocalError(t("errors.needSignalsMms")); return; }
    if (!sclFile)  { setLocalError(t("errors.needScl")); return; }
    setBusy(true);
    try {
      const id = `tab-${Date.now()}`;
      await runValidation(id, await readBytes(xlsxFile), xlsxFile.name, await readBytes(sclFile));
      usePagesStore.getState().open({ id, title: t("titles.sclValidation"), kind: "results" });
    } finally { setBusy(false); }
  };

  const needsIed = mode === "scratch" || mode === "assign";
  const showDropzone = mode === "assign" || mode === "validate";

  return (
    <div className="relative flex h-full flex-col overflow-y-auto p-8">
      <header className="absolute left-3 top-3 z-10 flex items-center gap-1.5">
        <a
          href="https://tools.axongroup.com.co/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-7 items-center gap-1.5 rounded px-2 text-sm font-medium text-[var(--color-on-surface-variant)] transition-colors hover:bg-[var(--color-surface-variant)] hover:text-[var(--color-on-surface)]"
        >
          {t("nav.gallery")}
          <Icon name="open_in_new" size={14} />
        </a>
      </header>
      <header className="absolute right-3 top-3 z-10 flex items-center gap-1.5">
        <AboutButton />
      </header>
      <div className="m-auto flex w-full flex-col items-center gap-8">
      <div className="text-center">
        <h1 className="text-3xl font-semibold text-[var(--color-brand)]">{t("title")}</h1>
        <p className="mt-1 text-sm text-[var(--color-on-surface-variant)]">
          {t("subtitle")}
        </p>
      </div>

      <div className="w-full max-w-xl rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-container)] overflow-hidden">
        {/* ── Pestañas de modo ── */}
        <div className="flex border-b border-[var(--color-border)]">
          {MODE_IDS.map(m => (
            <button key={m} onClick={() => switchMode(m)}
              className={"flex-1 px-3 py-2.5 text-xs font-medium transition-colors " +
                (mode === m
                  ? "border-b-2 border-[var(--color-brand)] text-[var(--color-brand)] bg-[var(--color-brand-soft-10)]"
                  : "text-[var(--color-on-surface-variant)] hover:bg-[var(--color-surface-variant)]")}>
              {t(`modes.${m}.label`)}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          {/* Descripción del modo activo */}
          <p className="text-xs text-[var(--color-on-surface-variant)] leading-relaxed">
            {t(`modes.${mode}.hint`)}
          </p>

          {/* IED — solo en modos que lo usan; opcional en 'scratch' */}
          {needsIed && (
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-[var(--color-on-surface-variant)]">
                {t("ied.label")} {needsIed && <span className="normal-case opacity-60">{t("ied.optional")}</span>}
              </label>
              <input
                type="text"
                list="ied-options-list"
                placeholder={t("ied.placeholder")}
                value={iedRef}
                onChange={e => {
                  const v = e.target.value;
                  const match = IED_OPTIONS.find(o => o.toLowerCase() === v.toLowerCase().trim());
                  setIedRef(match ?? v);
                }}
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 text-sm outline-none focus:border-[var(--color-brand)]"
              />
              <datalist id="ied-options-list">
                {IED_OPTIONS.map(ied => <option key={ied} value={ied} />)}
              </datalist>
            </div>
          )}

          {/* Dropzone — solo en modos con archivo */}
          {showDropzone && (
            <>
              <ReferenceDropzone onFiles={handleFiles}
                label={mode === "validate"
                  ? t("dropzone.validate")
                  : t("dropzone.assign")} />
              {fileNames.length > 0 && (
                <ul className="list-disc pl-4 text-xs text-[var(--color-on-surface-variant)]">
                  {fileNames.map(n => <li key={n}>{n}</li>)}
                </ul>
              )}
            </>
          )}

          {/* Acción principal — una sola, con nombre claro por modo */}
          <div className="flex justify-center pt-1">
            {mode === "scratch" && (
              <Button variant="primary" onClick={enterScratch}>{t("actions.enter")}</Button>
            )}
            {mode === "assign" && (
              <Button variant="primary" onClick={() => void runAssign()} disabled={busy}>
                {busy ? t("actions.classifying") : t("actions.classify")}
              </Button>
            )}
            {mode === "validate" && (
              <Button variant="primary" onClick={() => void runValidateScl()} disabled={busy}>
                {busy ? t("actions.validating") : t("actions.validate")}
              </Button>
            )}
          </div>

          {localError && (
            <p className="text-center text-xs text-[var(--color-status-absent)]">⚠ {localError}</p>
          )}
        </div>
      </div>

      <AxonResourcesCard />
      </div>
    </div>
  );
}
